import { AfterViewInit, Component, Input, OnInit, ViewChild, ViewEncapsulation, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ActionCompleteEventArgs } from '@syncfusion/ej2-angular-dropdowns';
import { GridComponent, PageSettingsModel, RowDeselectEventArgs, RowSelectEventArgs } from '@syncfusion/ej2-angular-grids';
import { DialogComponent, TooltipComponent } from '@syncfusion/ej2-angular-popups';
import { SelectedEventArgs, UploaderComponent, UploadingEventArgs } from '@syncfusion/ej2-angular-inputs';
import { EmitType } from '@syncfusion/ej2-base';
import { ToastrService } from 'ngx-toastr';
import { IApiTypedResult, IBusinessOperationQualifier, ILLGenericKeys, ITermByIndustryUsageAndCategory, Order, User } from '../../shared/models/index';
import { Media } from '../../shared/models/media.model';
import { AuthenticationService, AuthorizationService, OrderService, StorageManagerService } from '../../shared/services/index';
import { LLFormService } from '../../shared/services/ll-form.service';
import { SettingsService } from '../../shared/services/settings.service';
import { OrderAdminComponent } from '../order-admin/order-admin.component';
import { Utilities } from '../../shared/services/utilities';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { fadeInOut } from '../../shared/helpers/animations';

@Component({
    selector: 'order-step-2',
    templateUrl: './order-step-2.component.html',
    styleUrls: ['./order-step-2.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    animations: [fadeInOut],
})
export class OrderStep2Component implements OnInit, AfterViewInit, OnDestroy {

    // Primary key
    orderRevisionId: number;
    // Object model for Order
    order: Order;
    // Hold on to original values for these fields for saving later
    beforeOrderStatus: string;
    beforeConfirmedDate: Date;
    // Angular form that contains controls
    orderStep2Form: UntypedFormGroup;

    // Reference data list
    serviceCategoryToServiceTypeAssoc: IBusinessOperationQualifier[];
    // Full list of all service categories
    serviceCategories: ILLGenericKeys[];
    // Should we show description box
    showOrderDescription = true;

    // List of all employees in company
    allEmployeesInCompany: User[] = null;

    // Save button caption
    saveButtonCaption = 'Save as Draft';

    // LL Form grid
    showLlForm = true;
    @ViewChild('llFormGrid') public llFormGrid: GridComponent;
    @Input() llFormGridData: any[];
    llFormInitialPage: PageSettingsModel;
    llFormSortOptions: Object;
    llFormToolbarOptions: Object;
    llFormRecordCount: number;
    llFormGridSelectionOptions: object;
    // Summary table for selected LL form grid row
    llFormGridSelectedRowData: any = null;
    gridFormatOptions: object;

    // Baseline SPL grid
    @ViewChild('baselineSplGrid') public baselineSplGrid: GridComponent;
    @Input() baselineSplGridData: any[];
    baselineSplInitialPage: PageSettingsModel;
    baselineSplSortOptions: Object;
    baselineSplToolbarOptions: Object;
    baselineSplRecordCount: number;
    baselineSplLoading = true;
    baselineSplSearchForm: UntypedFormGroup;
    baselineSplSelectionOptions: object;
    // ID of record currently selected in grid
    baselineSplGridSelectedId: number;
    // When loading an order, first the llFormGrid gets populated,
    // then usually a row gets selected, then that triggers
    // baselineSplGrid to call an API to get data, then
    // bind that data to the grid.  Only after that can
    // we select the correct row of the baselineSplGrid
    // based on what's in the order object.
    // That's outside the normal ngInit and load process,
    // so set a flag and each time the baselineSplGrid
    // load is finished, check to see if it's the first time,
    // if so select the correct row.
    wasFirstTimeBaselineSplGridSelectionDone: boolean;
    bIsSelectedLLFormActive = true;


    // ------------------------------------------------------------
    // File upload
    // ------------------------------------------------------------

    // Angular object
    @ViewChild('fileUpload')
    public fileUpload: UploaderComponent;
    // Tells uploader what URL to post files to (gets set programmatically later)
    fileUploadAsyncSettings: any = null;
    // Pointer to drop element
    dropElement: HTMLElement = null;
    // Valid extensions to upload
    validFileExtensionsStr: string;


    // ------------------------------------------------------------
    // Order admin box
    // ------------------------------------------------------------
    @ViewChild('orderAdminComponent')
    orderAdmin: OrderAdminComponent;

    // Delivery doc associated with this order
    // (not the source docs)
    deliveryDoc: Media = null;

    // If user deletes an existing delivery doc,
    // there's no angular form changes, but we need to force
    // a page reload.
    deliveryDocStatusChange: boolean;

    // Source docs
    sourceDocs: Media[] = null;

    orderStatuses: ITermByIndustryUsageAndCategory[] = null;


    // ------------------------------------------------------------
    // Page state
    // ------------------------------------------------------------
    // Are we currently loading or saving?
    pageState: string = null;
    // Can the current user edit the current order?
    canEdit: boolean;
    // Does the current user have admin order (RT employee) permissions
    canOrderAdmin: boolean;
    // Can user request cancellation?
    canRequestCancellation: boolean;
    // Error message to display on bottom of screen.
    errorMessage = '';
    fileMinSize: number;
    fileMaxSize: number;

    // ------------------------------------------------------------
    // Post save settings
    // ------------------------------------------------------------
    // Save the order object, when finished then optionally finish a deliverDoc
    // upload.  There's a lot of async steps, so put information about
    // saving orders and docs into class variables that can be set
    // in saveData and accessed in postSaveData to tie everything together.

    // Message from server about saving the order
    postSaveDataMessage: string;
    // URL to go to after save is finished.  Note we need the
    // new OrderRevisionId of the newly saved order revision
    // to generate this URL (usually).
    postSaveDataRedirectUrl: string;
    // OrderRevisionId from after we save order changes (but we haven't uploaded files yet so we need this stored)
    postSaveDataNewOrderRevisionId: number;
    // Track if source docs are finished uploading after order save
    postSaveWaitingForSourceDocUpload: boolean;
    // Track if delivery docs are finished uploading after order save
    postSaveWaitingForDeliveryUpload: boolean;

    @ViewChild('tooltipDC')
    public tooltipDC: TooltipComponent;

    @ViewChild('tooltipSPL')
    public tooltipSPL: TooltipComponent;

    // Data Collection Form Selection
    @ViewChild('confirmDialog') public confirmDialog: DialogComponent;
    animationSettings: Object = { effect: 'Zoom' };
    position: object = { X: 'center', Y: 'center' };

  public clipmodecolumn = { class: 'e-tooltipcolumn' };
  sub: Subscription;
    ingestFileValue: boolean;

    constructor(private fb: UntypedFormBuilder,
        private activatedRoute: ActivatedRoute,
        private authenticationService: AuthenticationService,
        private authorizationService: AuthorizationService,
        public orderService: OrderService,
        private storageManagerService: StorageManagerService,
        private llService: LLFormService,
        private toastr: ToastrService,
        private router: Router,
        private utilService: Utilities,
        private cdRef: ChangeDetectorRef,
      private settingsService: SettingsService,
      private sanitizer: DomSanitizer) { }
      toHtml(input): any {
        return input ? new DOMParser().parseFromString(input, 'text/html').documentElement.textContent : '';
      }

    // ------------------------------------------------------------
    // Initialize
    // ------------------------------------------------------------

    ngOnInit(): void {
      this.pageState = this.orderService.PAGE_STATE_LOADING;

        // Users can't change companies in the middle of viewing/editing an order
        this.utilService.toggleClientDropdownState(true);

        // Force some permissions off in case of ng routing doesn't do a full refresh
        this.order = null;
        this.canEdit = false;
        this.canOrderAdmin = false;
        this.canRequestCancellation = false;
        this.deliveryDoc = null;
        this.deliveryDocStatusChange = false;
        this.sourceDocs = null;
        this.baselineSplGridSelectedId = null;
        this.wasFirstTimeBaselineSplGridSelectionDone = false;

        // Extract primary key from query string
        this.orderRevisionId = this.orderService.getNumberOrNull(this.activatedRoute.snapshot.params.orderRevisionId);

        // The guard says you need read permission to get to this page.
        // User might also have edit permissions.
        this.canEdit = this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_WRITE_ORDER);
        this.canOrderAdmin = this.authorizationService.hasPermission(this.orderService.PERMISSION_ORDER_ADMIN);

        // Pull in file upload validation settings from appsettings.json
        const settings = this.settingsService.getSettings();
        this.validFileExtensionsStr = settings.ValidFileExtensions;
        this.fileMinSize = settings.FileMinSize;
        this.fileMaxSize = settings.FileMaxSize;

        // Tell angular form what fields it has.
        this.orderStep2Form = this.fb.group({
            externalTrackingNumber: [''],
            orderDescription: [''],
            llFormGrid: [''],
            baselineSplGrid: [''],
            fileUpload: ['']
        });

        // set focus on the invisible div so the firest tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
       this.initTabIndex();

        // Get order data from the resolver and populate angular objects
        this.loadData();

        // Display settings for grids
        this.llFormToolbarOptions = ['Search'];
        this.llFormInitialPage = { currentPage: 1, pageSize: 10, pageSizes: [10, 25, 50, 75, 100, 'All'] };
        this.llFormGridSelectionOptions = { type: 'Single' };

        this.baselineSplInitialPage = { currentPage: 1, pageSize: 10, pageSizes: [10, 25, 50, 75, 100, 'All'] };
        this.baselineSplSelectionOptions = { type: 'Single' };

        this.gridFormatOptions = { type: 'dateTime', format: Utilities.getLocaleDateString() + ' hh:mm a' };

        // Hide grids if not service category Full or Partial
        this.showLlForm = [this.orderService.SERVICE_GROUP_ESG, this.orderService.SERVICE_GROUP_ER, this.orderService.SERVICE_GROUP_REMS, this.orderService.SERVICE_GROUP_LABEL_CONVERSION].includes(this.order.serviceGroup);
        // Show order description if service category is LDR, Report, or Publishing
        this.showOrderDescription = [this.orderService.SERVICE_GROUP_REPORT, this.orderService.SERVICE_GROUP_PUBLISHING].includes(this.order.serviceGroup);

        this.populateFormControls();

        this.fileUploadAsyncSettings = {
            saveUrl: this.orderService.getFileUploadUrl()
        };
        this.dropElement = document.getElementById('dropArea');

  }

  initTabIndex() {
    (document.activeElement as HTMLElement).blur();

    const main = document.getElementById('firstDiv');

    if (main) {
      main.focus();
    }
  }

  tooltipValue(args) {

    let grid;
    let currentGridId;

    let tooltip: TooltipComponent;

    currentGridId = args.event.currentTarget.firstChild.id;

    if (args.target.scrollWidth <= args.target.clientWidth) {
      args.cancel = true;
    } else {

      if (currentGridId) {
        switch (currentGridId) {
          case 'llFormGrid':
            grid = this.llFormGrid;
            tooltip = this.tooltipDC;
            break;
          case 'baselineSplGrid':
            grid = this.baselineSplGrid;
            tooltip = this.tooltipSPL;
            break;
        }

        const rowInfo = grid.getRowInfo(args.target);
        const rowData = rowInfo.rowData;
        tooltip.cssClass = 'customTooltip';
        tooltip.content =  this.toBrackets(rowData[(rowInfo.column as any).field]);
      }
    }
  }

  toBrackets(str) {
    const retVal = str.split('&lt;').join('<')
      .split('&gt;').join('>')
      .split('&amp;').join('&');
      // .split('&apos;').join("'")
      // .split('&quot;').join('"')
      // .split('&colon;').join(':')
      // .split('&excl;').join('!')
      // .split('&sol;').join('/')
      // .split('&bsol;').join('\\')
      // .split('&vert;').join('|')
      // .split('&quest;').join('?')
      // .split('&ast;').join('*');

     return retVal;
  }

  ngAfterViewInit() {
        // After grid is populated with initial data, select row that
        // corresponds to object. Do it after ngOnInit so the grid
        // has time to populate.
        // Note: wrap this code in a setTimeout to avoid angular error "expression was changed after it was checked"
        // see https://blog.angular-university.io/angular-debugging/
        setTimeout(() => {

            // Bind drop area to ejs-uploader
            this.dropElement = document.getElementById('dropArea');

            if (this.order && this.order.dataCollectionForm && this.llFormGridData) {
              // First check if the current selected Data Collection form are associated with the latest LL Revision
            const currentDataCollectionForm = this.llFormGridData.filter(x => x.llRevisionId === this.order.dataCollectionForm);
              if (currentDataCollectionForm && currentDataCollectionForm.length > 0) {
              // Loop through all rows of grid data to find match, then force grid selected index
              for (let i = 0; i <= this.llFormGridData.length; i++) {
                if (this.llFormGridData[i]) {
                  if (this.llFormGrid && (this.llFormGridData[i].llRevisionId === this.order.dataCollectionForm)) {
                    this.llFormGrid.selectedRowIndex = i;
                    this.llFormGridSelectedRowData = this.llFormGridData[i];
                    break;
                  }
                }
              }
                if (this.llFormGrid && this.llFormGrid.selectedRowIndex >= 0) {
                    this.getBaselineSpls(this.llFormGridData[this.llFormGrid.selectedRowIndex].llRevisionId);
                }
            } else { // The selected dataCollectionFrom is associated with an older LL revisionID
              this.bIsSelectedLLFormActive = false;
              this.getLLByRevisionId(this.order.dataCollectionForm);
            }
                // We have to still populate the dependent baselineSpl grid.
                // However, when the load process selects a row from the llFormGrid,
                // that will trigger loading the baselineSpl grid data,
                // so just let it happen later.
            }

            this.pageState = this.orderService.PAGE_STATE_READY;

            // Update grid selection permission
            if (this.llFormGrid) {
                this.llFormGrid.allowSelection = this.canEdit;
            }
            if (this.baselineSplGrid) {
                this.baselineSplGrid.allowSelection = this.canEdit;
          }
        });

        if (this.order && this.orderAdmin) {
            if (this.order.serviceCategory === this.orderService.SERVICE_CATEGORY_FULL_CONVERSION  || this.order.serviceCategory === this.orderService.SERVICE_CATEGORY_PARTIAL_CONVERSION) {
                this.orderAdmin.showIngestFile(true);
            } else {
                this.orderAdmin.showIngestFile(false);
            }
        }

        (this.fileUpload as any).isForm = false;
        this.cdRef.detectChanges();
    }


    // ------------------------------------------------------------
    // Load data
    // ------------------------------------------------------------

    // Get data from the resolver and populate various dropdownlists
    // and main order fields and store it in class state.
    loadData(): void {
        // Get data from resolver used in routing module
        const orderDataResolved = this.activatedRoute.snapshot.data['orderDataResolved'];
        if (orderDataResolved.error) {
            this.toastr.error(orderDataResolved.error);
        } else {
            if (orderDataResolved) {
                // OrderResolver returns a fixed array of data.

                // Employees of this company
                if (!orderDataResolved[0]) {
                    this.orderService.handleOrderError(this, null, 'Failed to get list of employees in company', false);
                }
                this.allEmployeesInCompany = orderDataResolved[0];

                // Main order
                if (!orderDataResolved[3]) {
                    this.orderService.handleOrderError(this, null, 'Failed to load order', false);
                }

                let orderApiResult: IApiTypedResult<Order> = orderDataResolved[3];
                if (orderApiResult) {
                    if (orderApiResult.isSuccess) {
                        this.order = orderApiResult.data;
                        // Remember original values for certain fields
                        this.beforeOrderStatus = this.order.orderStatus;
                        this.beforeConfirmedDate = this.order.confirmedDueDate;

                        // Only user with "SSDP.CanUpateOrder" permission can edit an order if the order status is "Order Delivered - Awaiting Approval" or "completed"
                        if (!this.canOrderAdmin && (this.order.orderStatus === this.orderService.ORDER_STATUS_ORDER_DELIVERED_AWAITING_APPROVAL
                            || this.order.orderStatus === this.orderService.ORDER_STATUS_ORDER_COMPLETE)) {
                            this.canEdit = false;
                        }

                        // Don't allow canOrderAdmin if draft
                        if (this.order.orderStatus === this.orderService.ORDER_STATUS_DRAFT) {
                            this.canOrderAdmin = false;
                        }

                        // If still canOrderAdmin, recaption save
                        if (this.canOrderAdmin) {
                            this.saveButtonCaption = 'Update Order';
                        }

                        // Non-admin users can cancel pending or inprocess if they have the perm
                        if (!this.canOrderAdmin
                            && ([this.orderService.ORDER_STATUS_PENDING, this.orderService.ORDER_STATUS_IN_PROCESS].indexOf(this.order.orderStatus) > -1)
                            && this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_CANCEL_ORDER)) {
                            this.canRequestCancellation = true;
                        }

                        // Pull out the deliveryDoc and sourceDocs separately
                        // order.sourceDocuments is an array mixed with both deliveryDoc and SourceDocs.
                        if (this.order && this.order.sourceDocuments) {
                            this.deliveryDoc = null;
                            let deliveryDocs = this.order.sourceDocuments.filter(x => x.mediaFileType === this.orderService.UPLOAD_FILE_TYPE_DELIVERY);
                            if (deliveryDocs && deliveryDocs.length > 0) {
                                this.deliveryDoc = deliveryDocs[0];
                            }
                            // Step 2 - sourceDocs
                            (this.order.sourceDocuments as any).find(x => x.mediaFileType === this.orderService.UPLOAD_FILE_TYPE_DELIVERY);
                            this.sourceDocs = this.order.sourceDocuments.filter(x => x.mediaFileType === this.orderService.UPLOAD_FILE_TYPE_SOURCE_DOC);

                            // Special case - grab the list of files inside the order,
                            // and add a property to each subrecord with index 0, 1, 2, ...
                            // to make interaction with angular easier.
                            if (this.sourceDocs) {
                                for (let i = 0; i < this.sourceDocs.length; i++) {
                                    const myDoc: any = this.sourceDocs[i];
                                    myDoc.index = i;
                                }
                            }
                        }
                    } else {
                        this.orderService.handleOrderError(this, orderApiResult, 'Failed to load order', false);
                    }
                }

                if (!orderDataResolved[5]) {
                    this.orderService.handleOrderError(this, null, 'Failed to load order statuses', false);
                }
                this.orderStatuses = orderDataResolved[5];

                if (!orderDataResolved[6]) {
                    this.orderService.handleOrderError(this, null, 'Failed to load service categories', false);
                }
                this.serviceCategories = orderDataResolved[6];
            }

            // Update LL forms array (take only active records)
            if (orderDataResolved[4]) {
                this.llFormGridData = orderDataResolved[4].data.filter(x => x.isDraftMode === false);
            }
        }
    }
    // end loadData



    // Baseline SPL grid is dependent on LL form grid
    // Everytime someone clicks on ll form grid, do a database lookup
    // to get corresponding baseline spls
    getBaselineSpls(llRevisionId: number) {
        if (llRevisionId) {
            this.baselineSplGridData = null;

            this.orderService.getBaselineSplData(llRevisionId).subscribe(data => {
                if (data && data['data'].length > 0) {
                    this.baselineSplLoading = false;
                    this.baselineSplGridData = data['data'];
                    this.baselineSplRecordCount = this.baselineSplGridData.length;
                    // See comments where this field is defined for explanation
                    if (this.order && this.order.baselineWorkFlowOrderId) {
                        if (!this.wasFirstTimeBaselineSplGridSelectionDone) {
                            this.wasFirstTimeBaselineSplGridSelectionDone = true;
                                // Loop through all rows of baseline Spl data to find match, then force grid selected index
                                for (let i = 0; i <= this.baselineSplGridData.length; i++) {
                                    if (this.baselineSplGridData[i].workflowOrderId === this.order.baselineWorkFlowOrderId) {
                                        this.baselineSplGrid.selectedRowIndex = i;
                                        this.baselineSplGridSelectedId = this.baselineSplGridData[i].workflowOrderId;
                                        this.cdRef.detectChanges();
                                        break;
                                    }
                                }

                            // setTimeout(() => {
                            //    // Loop through all rows of baseline Spl data to find match, then force grid selected index
                            //    for (let i = 0; i <= this.baselineSplGridData.length; i++) {
                            //      if (this.baselineSplGridData[i].mediaRevisionId === this.order.completedSPL) {
                            //            this.baselineSplGrid.selectedRowIndex = i;
                            //          this.baselineSplGridSelectedId = this.baselineSplGridData[i].mediaRevisionId;
                            //          this.cdRef.detectChanges();
                            //            break;
                            //        }
                            //    }
                            // }, 100);
                          }
                    }
                }
            },
                error => {
                    this.baselineSplLoading = false;
                    this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
                }
            );
        }
    }

    // Get LL form high leve detail by revision Id
    getLLByRevisionId(llRevisionId: number) {
      if (llRevisionId) {
          this.llService.getLLHighLevlInfoById(llRevisionId.toString()).subscribe(data => {
              if (data && data['data']) {
                this.llFormGridSelectedRowData = data['data'];
                this.getBaselineSpls(llRevisionId);
              }
          },
              error => {
                  this.baselineSplLoading = false;
                  this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
              }
          );
      }
  }

    // ------------------------------------------------------------
    // Populate form controls
    // ------------------------------------------------------------

    // Populate the order data
    populateFormControls(): void {
        this.orderStep2Form.reset();
        this.orderStep2Form.patchValue({
            externalTrackingNumber: this.order.externalTrackingNumber,
            orderDescription: this.order.orderDescription,
            // Can't patch grids here, but you can set row index in AfterViewInit
        });
    }


    // ------------------------------------------------------------
    // Grids
    // ------------------------------------------------------------
  llFormGridRowSelected(args: RowSelectEventArgs) {
    if (args.isInteracted) {
        this.llFormGridSelectedRowData = null;
        this.bIsSelectedLLFormActive = true;
        const selectedRecords: any[] = this.llFormGrid.getSelectedRecords();
        if (selectedRecords && selectedRecords.length > 0) {
          this.llFormGridSelectedRowData = selectedRecords[0];
          this.getBaselineSpls(selectedRecords[0].llRevisionId);
        }
        this.orderStep2Form.markAsDirty();
        this.orderStep2Form.markAsTouched();
      }
    }

    llFormGridRowDeselected(args: RowDeselectEventArgs) {
        this.llFormGridSelectedRowData = null;
        this.orderStep2Form.controls.llFormGrid.markAsDirty();
        this.getBaselineSpls(null);
        if (args.isInteracted) {
            this.orderStep2Form.markAsDirty();
            this.orderStep2Form.markAsTouched();
        }
    }

    baselineSplGridRowSelected(args: RowSelectEventArgs) {
        this.baselineSplGridSelectedId = null;
        const selectedRecords: any[] = this.baselineSplGrid.getSelectedRecords();
        if (selectedRecords && selectedRecords.length > 0) {
            this.baselineSplGridSelectedId = selectedRecords[0].workflowOrderId;
        }
        if (args.isInteracted) {
            this.orderStep2Form.markAsDirty();
            this.orderStep2Form.markAsTouched();
        }
    }

    baselineSplGridRowDeselected(args: RowDeselectEventArgs) {
        this.baselineSplGridSelectedId = null;
        if (args.isInteracted) {
            this.orderStep2Form.markAsDirty();
            this.orderStep2Form.markAsTouched();
        }
    }

    // Change grid filter operator from "startwith" to "contains" for columns that are string
    public begin(args: any): void {
        if (args.requestType === 'filtering') {
            if (args.columns) {
                args.columns.forEach(col => {
                    switch (args.currentFilteringColumn) {
                        case 'modifiedDate':
                            break;
                        default:
                            col.operator = 'contains';       // set the “contains” operator for the column
                            break;
                    }
                });
            }
        }
    }

    // Display detail of a single LL form in a separate tab
    viewLLForm(llRevisionId: number) {
        if (llRevisionId) {
            this.router.navigate([])
                .then(result => {
                    window.open(`/products/ll-form/${llRevisionId}`, '_blank');
                });
        }
    }

    // ------------------------------------------------------------
    // Existing file list
    // ------------------------------------------------------------
    deleteExistingFile(index: number) {
      this.order.sourceDocuments[index].isDeleted = true;
      this.orderStep2Form.markAsDirty();
      this.orderStep2Form.markAllAsTouched();
    }

    undeleteExistingFile(index: number) {
        this.order.sourceDocuments[index].isDeleted = false;
        this.orderStep2Form.markAsDirty();
        this.orderStep2Form.markAllAsTouched();
    }


    // ------------------------------------------------------------
    // File upload/download
    // ------------------------------------------------------------

    // Trigger a browse window
    browseClick() {
        document.getElementsByClassName('e-file-select-wrap')[0].querySelector('button').click(); return false;
    }

    public onSelected: EmitType<SelectedEventArgs> = (args: any) => {
        // Mark as dirty so we know user has to hit save button
        this.orderStep2Form.markAsDirty();
        this.orderStep2Form.markAsTouched();
    }

    // Upload sourceDoc file to order, validate settings
    public onUploading: EmitType<UploadingEventArgs> = (args: any) => {
        this.orderService.uploadOrderDoc(args,
            this.order.orderId,
            this.postSaveDataNewOrderRevisionId,
            this,
            this.orderStep2Form,
            false,
            this.orderService.UPLOAD_FILE_TYPE_SOURCE_DOC);
    }

    // Successfully uploaded one sourceDoc file.
    public onSuccess: EmitType<Object> = (args: any) => {
    }

    // For sourceDocs
    // Called after *ALL* files or uploaded, or after one file has an error
    // (yeah that seems weird, we have to do lots of "if" statements to sort through that)
    public onActionComplete: EmitType<ActionCompleteEventArgs> = (args: any) => {
        let atLeastOneGoodUpload = false;
        if (args && args.fileData) {
            for (const myFileData of args.fileData) {
                if ((myFileData as any).statusCode === '2') {
                    // Got at least one good upload
                    atLeastOneGoodUpload = true;
                }
            }
        }

        if (atLeastOneGoodUpload) {
            // We got the files, stop waiting for them.
            this.postSaveWaitingForSourceDocUpload = false;
            this.postSaveData();
        }
        this.pageState = this.orderService.PAGE_STATE_READY;
    }

    // upload failure for sourceDocs
    onUploadFailure(event) {
        this.toastr.error(`Error during upload.  $event`);
    }

    // download sourceDoc
    downloadFile(filePath: string, fileName: string) {
        this.orderService.downloadFile(filePath, fileName).subscribe(response => {
            this.orderService.postDownloadFile(response, fileName);
        },
          error => {
            this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
          });
    }

    onIngestFilechanged(event: boolean) {
        this.ingestFileValue = event;
    }

    // ------------------------------------------------------------
    // Order admin is a subcomponent, and emits up certain events
    // usually about the delivery doc
    // ------------------------------------------------------------
    onOrderAdminFileUpload(event) {
        if (event === 'success') {
            // Successfully uploaded delivery doc
            this.postSaveWaitingForDeliveryUpload = false;
            this.postSaveData();
        } else if (event === 'failure') {
            // Attempted to upload delivery doc but failed
            this.pageState == this.orderService.PAGE_STATE_READY;
            this.toastr.error('Unexpected error uploading delivery document');
        } else if (event === 'deleteExistingDeliveryDoc') {
            // Request that existing delivery doc be deleted with next save action
            if (this.deliveryDoc) {
                this.deliveryDocStatusChange = true;
                this.deliveryDoc.isDeleted = true;
            }
            this.pageState == this.orderService.PAGE_STATE_READY;
        } else if (event === 'undeleteExistingDeliveryDoc') {
            // Undo request that existing delivery doc be deleted with next save action
            if (this.deliveryDoc) {
                this.deliveryDocStatusChange = false;
                this.deliveryDoc.isDeleted = false;
            }
            this.pageState == this.orderService.PAGE_STATE_READY;
        } else if (event === 'onIngestFileChange') {

            if (this.deliveryDoc) {
                this.deliveryDoc.ingestFile = this.ingestFileValue;
            }
            this.pageState == this.orderService.PAGE_STATE_READY;

        }
    }

    // ------------------------------------------------------------
    // Save data
    // ------------------------------------------------------------
    saveData(nextPage: string) {
        this.pageState = this.orderService.PAGE_STATE_SAVING;

        // Clear out data from prior save
        this.postSaveDataMessage = null;
        this.postSaveDataNewOrderRevisionId = null;
        this.postSaveDataRedirectUrl = null;
        this.postSaveWaitingForDeliveryUpload = false;
        this.postSaveWaitingForSourceDocUpload = false;

        // Where to go after save
        let nextPageUrl = '';
        switch (nextPage) {
            case 'STEP1':
                nextPageUrl = '/order-step-1';
                break;
            case 'STEP2':
                nextPageUrl = '/order-step-2';
                break;
            case 'STEP3':
                nextPageUrl = '/order-step-3';
                break;
        }

        this.errorMessage = '';

        const modifiedByUser = this.storageManagerService.read('userData').name;

        if (nextPage === 'CANCEL') {
            // Request order cancellation is different enough let's handle it separately.
            this.orderService.requestOrderCancellation(this.canRequestCancellation, this.order, modifiedByUser, '/order-step-2', false);
            this.pageState = this.orderService.PAGE_STATE_READY;
        } else {
            let dirty = this.orderService.isAngularFormDirty(this.orderStep2Form);
            // Also check fields in order admin box
            if (this.canOrderAdmin && this.orderAdmin.orderAdminForm) {
                dirty = dirty || this.orderService.isAngularFormDirty(this.orderAdmin.orderAdminForm);
            }

            // If you can't edit, it can't be dirty.
            // Sometimes updating grid stuff during page load sets the dirty flag
            if (!this.canEdit && !this.canOrderAdmin) {
                dirty = false;
            }

            // stop here if form is invalid
            if (this.orderStep2Form.invalid) {
                this.pageState = this.orderService.PAGE_STATE_READY;
                this.toastr.warning('Data is invalid.');
                return;
            } else {
                // Only save if dirty
                if (dirty && (this.canEdit || this.orderAdmin)) {
                    // Put back in prior values for certain fields
                    this.order.beforeOrderStatus = this.beforeOrderStatus;
                    this.order.beforeConfirmedDate = this.beforeConfirmedDate;

                    // Before we save any data, check if there are files waiting to be uploaded
                    let sourceDocsWaitingToBeUploaded = false;
                    if (this.fileUpload && this.fileUpload.getFilesData() && this.fileUpload.getFilesData().length) {
                        sourceDocsWaitingToBeUploaded = true;
                    }
                    let deliveryDocsWaitingToBeUploaded = false;
                    if (this.canOrderAdmin && this.orderAdmin.adminFileUpload && this.orderAdmin.adminFileUpload.getFilesData() && this.orderAdmin.adminFileUpload.getFilesData().length > 0) {
                        deliveryDocsWaitingToBeUploaded = true;
                    }

                    // Save the order attributes first
                    this.order.externalTrackingNumber = this.orderStep2Form.controls.externalTrackingNumber.value;
                    this.order.orderDescription = this.orderStep2Form.controls.orderDescription.value;

                    // Pull the ID of the selected LL Form grid
                    this.order.dataCollectionForm = null;
                    if (this.llFormGridSelectedRowData) {
                        this.order.dataCollectionForm = this.llFormGridSelectedRowData.llRevisionId;
                    }

                    // Pull the ID of the baseline SPL
                    this.order.baselineWorkFlowOrderId = null;
                    if (this.baselineSplGridSelectedId) {
                        this.order.baselineWorkFlowOrderId = this.baselineSplGridSelectedId;
                    }

                    // Save the admin fields if we're allowed
                    if (this.order && this.canOrderAdmin) {
                        this.order.orderStatus = this.orderAdmin.orderAdminForm.controls.orderStatus.value;
                        this.order.confirmedDueDate  = this.utilService.getLocalDateString(this.orderAdmin.orderAdminForm.controls.confirmedDueDate.value);
                        if (deliveryDocsWaitingToBeUploaded) {
                            // DeliveryDoc supplied, mark the actual delivery date
                            this.order.actualDeliveryDate = this.utilService.getLocalDateString(new Date());
                        } else if (this.deliveryDoc && this.deliveryDoc.isDeleted) {
                            // DeliveryDoc was requested to be deleted, blank
                            // out actual delivery date
                            this.order.actualDeliveryDate = null;
                        }
                    }

                    // Save to database
                    this.orderService.saveOrder(this.order, modifiedByUser).subscribe((response) => {
                        // Coerce to specific type
                        const responseTyped: IApiTypedResult<Order> = response as IApiTypedResult<Order>;
                        if (responseTyped.isSuccess) {
                            // Gather up settings so after async upload finishes we know where to go
                            this.postSaveDataMessage = responseTyped.message;
                            this.postSaveDataNewOrderRevisionId = responseTyped.data.orderRevisionId;
                            this.postSaveDataRedirectUrl = `${nextPageUrl}/${this.postSaveDataNewOrderRevisionId}`;

                            if (nextPage === 'SUBMIT') {
                                // Step 2 doesn't have submit, but steps 1, 2, 3 save methods
                                // are so complex, it makes my life easier to keep the same structure
                                // across all
                                this.toastr.error('invalid submit');
                                return;
                            } else {
                                // Regular save of Order object successful, now do optional file uploads.
                                //
                                // We just set the property on this component postSavedatNewOrderRevisionId
                                // which is shared with the child component OrderAdminComponent,
                                // sounds great BUT angular timing messes it up.  The child component
                                // can't see the value immediately.  The child component can't do setTimeout
                                // because the upload is already in progress.  So, we make the parent setTimeout
                                // (sleep) right now so angular child component catches up with bound value for
                                // and can save the uploaded doc to correct postSavedatNewOrderRevisionId
                                setTimeout(() => {

                                    // Now that the order is saved, upload docs to *new* OrderRevisionId from
                                    // potentially sourceDocs and delivery (inside child component)
                                    if (!sourceDocsWaitingToBeUploaded && !deliveryDocsWaitingToBeUploaded) {
                                        // No files to upload, we're done.
                                        this.postSaveData();
                                    } else {
                                        // Upload sourceDoc files from this page
                                        if (sourceDocsWaitingToBeUploaded) {
                                            this.postSaveWaitingForSourceDocUpload = true;
                                            this.fileUpload.upload(this.fileUpload.getFilesData()); // this is async
                                        }
                                        // Asynchronously upload delivery file from admin child componet
                                        if (deliveryDocsWaitingToBeUploaded) {
                                            // upload the files
                                            this.postSaveWaitingForDeliveryUpload = true;
                                            this.orderAdmin.adminFileUpload.upload(this.orderAdmin.adminFileUpload.getFilesData());  // this is async
                                        }
                                        // Nothing left for now, wait for events to get triggered.
                                        return;
                                    }
                                }, 100);
                            }
                        } else {
                            this.orderService.handleOrderError(this, responseTyped, 'Order save failed', true);
                        }
                    },
                        error => this.orderService.handleOrderError(this, error, 'Order save failed', true)
                    );
                    this.orderStep2Form.markAsPristine();
                } else {
                    this.pageState = this.orderService.PAGE_STATE_READY;
                    // if no changes to save, but user wants to go to next page, that's fine
                    if (nextPage !== 'STEP2') {
                        // Force angular to rerun ngOnInit to reload data when navigating to address
                        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
                        this.router.onSameUrlNavigation = 'reload';

                        this.router.navigate([`${nextPageUrl}/${this.orderRevisionId}`]).then(() => {
                            this.pageState = this.orderService.PAGE_STATE_READY;
                            window.scrollTo(0, 0);
                        });
                    } else {
                        this.pageState = this.orderService.PAGE_STATE_READY;
                        this.toastr.warning('No changes to save.');
                    }
                }
            }
        }
    }

    // After order is saved AND all files are uploaded via order admin
    // and Step 2 file uploader (not easy because they are all async)
    // then go ahead and give a message and redirect.
    // Only step 2 has dual file uploaders - source & delivery,
    // but for consistency I'm copy/pasting this function across
    // all three steps.
    postSaveData() {
        // Are we still waiting for sourceDoc upload or delivery upload?
        if (this.postSaveWaitingForSourceDocUpload || this.postSaveWaitingForDeliveryUpload) {
            // Yup, keep waiting.
            return;
        }

        this.toastr.success(this.postSaveDataMessage);

        // Force angular to rerun ngOnInit to reload data when navigating to address
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.onSameUrlNavigation = 'reload';

        this.router.navigate([this.postSaveDataRedirectUrl]).then(() => {
            this.pageState = this.orderService.PAGE_STATE_READY;
            // Force a full form reset to
            // make sure file upload control clears out.
            if (this.fileUpload) {
                this.fileUpload.clearAll();
            }
            // Clear out admin file upload control if relevant
            if (this.orderAdmin && this.orderAdmin.adminFileUpload) {
                this.orderAdmin.adminFileUpload.clearAll();
            }
            window.scrollTo(0, 0);
        });
    }

    confirmDlgButtonsBtnClickNo: EmitType<object> = () => {
        this.confirmDialog.hide();
    }

    confirmDlgButtonsBtnClickYes: EmitType<object> = () => {
        this.confirmDialog.hide();
        this.saveData('STEP3');
    }
    confirmDlgButtons: Object[] = [{ click: this.confirmDlgButtonsBtnClickNo.bind(this), buttonModel: { content: 'Cancel' } }, { click: this.confirmDlgButtonsBtnClickYes.bind(this), buttonModel: { content: 'Continue', isPrimary: true } }];

    // If a Data Collection Form has not been selected for an Order of the following type then generate a warning message
    validateDataCollectionSelection() {
        if ((this.order.serviceCategory === "Full Conversion" || this.order.serviceCategory === "Partial Conversion") && !this.llFormGridSelectedRowData) {
            this.confirmDialog.show();
        } else {
            this.saveData('STEP3')
        }
    }

    // ------------------------------------------------------------
    // Misc
    // ------------------------------------------------------------

  public onCreated() {
      if (this.fileUpload) {
        (this.fileUpload as any).isForm = false;
      }
    }

    public onRemove() {
        this.orderStep2Form.markAsDirty();
        this.orderStep2Form.markAsTouched();
      }

    // Add CORS headers - for file uploads
    public addCorsHeaders(args: any) {
        args.currentRequest.setRequestHeader('Access-Control-Allow-Origin', '*');
    }

    // convenience getter for easy access to form fields
    get f() { return this.orderStep2Form.controls; }

    // ------------------------------------------------------------
    // Destroy
    // ------------------------------------------------------------
    ngOnDestroy(): void {
        this.utilService.toggleClientDropdownState(false);
    }
}
